import React, { createContext, Dispatch, useContext, useReducer } from 'react';

import Loading from '../../../context/Loading';
import { PageAction, usePageActions } from '../actions';
import { pageReducer } from '../reducers';
import { initialPageState, PageState } from '../state/initialPageState';

interface PageStoreContextValue {
    pageState: PageState;
    pageDispatch: Dispatch<PageAction>;
    pageActions: ReturnType<typeof usePageActions>;
}

const PageStoreContext = createContext<PageStoreContextValue | undefined>(undefined);

function usePageStore(): PageStoreContextValue {
    const ctx = useContext(PageStoreContext);
    if (!ctx) {
        throw new Error('usePageStore must be used inside PageStoreProvider');
    }
    return ctx;
}

const PageStoreProvider = ({ children }: { children: React.ReactNode }) => {
    const reducer = React.useCallback(pageReducer, []);
    const [state, dispatch] = useReducer(reducer, initialPageState);
    const actions = usePageActions(state, dispatch);
    const contextValue = React.useMemo(
        () => ({
            pageState: state,
            pageDispatch: dispatch,
            pageActions: actions
        }),
        [state, dispatch, actions]
    );

    return (
        <PageStoreContext.Provider value={contextValue}>
            <Loading />
            {children}
        </PageStoreContext.Provider>
    );
};

export { PageStoreContext, usePageStore, PageStoreProvider };
